import React, { useState } from "react";

import { FaCheck, FaPlus, FaMinus } from "react-icons/fa";

import { ButtonSecondary } from "../UI/Button";
import { openCalendly } from "../Product-LMS/calendly";

const audience = [
  {
    title: "Universities & Colleges",
    text: "Semester exams, entrance tests, and admissions",
  },
  {
    title: "Corporates",
    text: "Employee certifications, skill assessments, and recruitment tests",
  },
  {
    title: "Certification & Training Bodies",
    text: "Large-scale, high-stakes certification exams",
  },
  {
    title: "Government & Public Sector",
    text: "Recruitment and departmental exams",
  },
  {
    title: "EdTech & Training Companies",
    text: "Assessments for online courses",
  },
];

const features = [
  {
    title: "AI-Based Live Proctoring",
    text: "Real-time detection of suspicious behaviour such as multiple faces, face absence, and unauthorized objects",
  },
  {
    title: "Candidate Authentication",
    text: "ID and face verification before the exam begins",
  },
  {
    title: "Secure Browser / Lockdown Mode",
    text: "Blocks tab switching, copy-paste, screen sharing, and unauthorized apps",
  },
  {
    title: "Screen, Webcam & Audio Monitoring",
    text: "Continuous capture with session recordings for later review",
  },
  {
    title: "Flexible Proctoring Modes",
    text: "AI-only, live proctor, or recorded review, depending on exam stakes",
  },
  {
    title: "Customizable Rules",
    text: "Configure proctoring strictness per exam type",
  },
  {
    title: "LMS Integration",
    text: "Works with your existing LMS (Moodle and others), so there is no change to your learning workflow",
  },
  {
    title: "Scalable Delivery",
    text: "From a few hundred to thousands of concurrent candidates",
  },
  {
    title: "Instant Reports & Analytics",
    text: "Incident flags, candidate-wise reports, and an admin dashboard",
  },
];

const steps = [
  {
    title: "Create Your Exam",
    text: "We set up the exam and configure proctoring rules to your requirements.",
  },
  {
    title: "Invite Candidates",
    text: "Candidates receive secure links and clear instructions.",
  },
  {
    title: "Monitor in Real Time",
    text: "AI (and live proctors, if opted for) track every session.",
  },
  {
    title: "Review & Report",
    text: "Get flagged-incident reports, recordings, and results to confirm exam integrity.",
  },
];

const whyItems = [
  {
    title: "Single point of contact:",
    text: "we manage deployment, integration, and support end to end",
  },
  {
    title: "Tailored to your needs:",
    text: "configuration matched to your exam format, scale, and compliance requirements",
  },
  {
    title: "Seamless integration:",
    text: "we connect the platform with your existing systems",
  },
  {
    title: "Dedicated exam-day support:",
    text: "technical assistance for both admins and candidates",
  },
  {
    title: "Cost-effective:",
    text: "no exam centres, no travel, no logistics overhead",
  },
  {
    title: "Secure by design:",
    text: "encrypted data, strict access controls, and authenticated candidates",
  },
];

const benefits = [
  ["Cheating & impersonation", "AI monitoring + identity verification"],
  ["High exam centre costs", "Conduct exams from anywhere"],
  ["Manual invigilation effort", "Automated monitoring and reports"],
  ["Scaling for large batches", "Cloud-based, high concurrency"],
  ["Result credibility", "Recorded sessions + audit trail"],
];

const faqs = [
  {
    question: "Do candidates need to install anything?",
    answer:
      "Typically they need a compatible browser or secure exam application and a working webcam and microphone. We share a simple system-check before the exam.",
  },
  {
    question: "Can it integrate with our LMS?",
    answer:
      "Yes. We integrate with popular LMS platforms so exams run within your existing setup.",
  },
  {
    question: "Is live human proctoring available?",
    answer:
      "Yes. You can choose AI-only, live-proctored, or recorded-review modes based on exam importance.",
  },
  {
    question: "How many candidates can take an exam at once?",
    answer:
      "The platform is cloud-based and built to scale. We'll size it to your expected concurrency.",
  },
  {
    question: "How is candidate data protected?",
    answer:
      "Data is encrypted and access is restricted to authorized users only.",
  },
  {
    question: "Can we run a pilot first?",
    answer: "Of course. Contact us for a demo or a small pilot exam.",
  },
];

const SectionHeading = ({ children }) => (
  <h2 className="text-2xl lg:text-4xl font-bold text-quaternary text-center pb-8 lg:pb-10">
    {children}
  </h2>
);

const IntroCard = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 border-l-4 border-l-quaternary shadow-sm p-6 lg:p-8">
    <h2 className="text-2xl lg:text-3xl font-bold text-quaternary pb-5">
      {title}
    </h2>
    <div className="text-secondary leading-7 space-y-4">{children}</div>
  </div>
);

const FeatureCard = ({ title, text }) => (
  <div className="bg-white rounded-xl border border-gray-200 border-t-4 border-t-quaternary shadow-sm p-6 lg:p-8 h-full">
    <h3 className="text-lg font-bold text-primary pb-4">{title}</h3>
    <p className="text-secondary leading-7">{text}</p>
  </div>
);

const Content = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <section className="container text-primary py-12 lg:py-16">
        <IntroCard title="Conducting exams online is easy. Conducting them fairly is the real challenge.">
          <p>
            Exam Online combines AI-based monitoring, secure exam delivery, and
            detailed reporting so every candidate is verified, every session is
            monitored, and every result can be trusted.
          </p>
          <p>
            As your system integrator, IQSPL handles the whole journey:
            requirement study, setup, LMS integration, candidate onboarding,
            exam-day support, and post-exam reporting. You get one accountable
            partner instead of managing multiple vendors.
          </p>
        </IntroCard>
      </section>

      <section className="bg-gray-50 py-12 lg:py-16">
        <div className="container text-primary">
          <SectionHeading>Who It's For</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {audience.map((item) => (
              <FeatureCard key={item.title} {...item} />
            ))}
          </div>
        </div>
      </section>

      <section className="container text-primary py-12 lg:py-16">
        <SectionHeading>Key Features</SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((item) => (
            <FeatureCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="bg-gray-50 py-12 lg:py-16">
        <div className="container text-primary">
          <SectionHeading>How It Works</SectionHeading>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <li key={step.title} className="relative">
                <div className="flex items-center pb-4">
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-quaternary text-tertiary font-bold shrink-0">
                    {index + 1}
                  </span>
                  {index < steps.length - 1 && (
                    <span className="hidden lg:block h-0.5 flex-1 ml-3 -mr-8 bg-gray-300" />
                  )}
                </div>
                <h3 className="text-lg font-bold text-primary pb-2">
                  {step.title}
                </h3>
                <p className="text-secondary leading-7">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="container text-primary py-12 lg:py-16">
        <SectionHeading>Why Choose IQSPL for Proctoring Services?</SectionHeading>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5">
          {whyItems.map((item) => (
            <li key={item.title} className="flex gap-3 leading-7">
              <FaCheck className="shrink-0 mt-1.5 text-quaternary" />
              <span className="text-secondary">
                <strong className="text-primary">{item.title}</strong>{" "}
                {item.text}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-gradient-to-br from-[#0b2b4f] to-quaternary py-12 lg:py-16">
        <div className="container text-center">
          <h2 className="text-2xl lg:text-4xl font-bold text-tertiary pb-6">
            Security &amp; Data Protection
          </h2>
          <p className="text-white/80 leading-7 max-w-3xl mx-auto lg:text-lg">
            Your exam content and candidate data stay protected through data
            encryption in transit and at rest, strict role-based access
            controls, candidate authentication before every exam, and a secure
            exam environment that prevents unauthorized resource access.
          </p>
        </div>
      </section>

      <section className="container text-primary py-12 lg:py-16">
        <SectionHeading>Benefits at a Glance</SectionHeading>
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="w-full min-w-[480px] text-left">
            <thead>
              <tr className="bg-quaternary text-tertiary">
                <th className="px-5 py-4 font-semibold">Challenge</th>
                <th className="px-5 py-4 font-semibold">
                  How Exam Online Helps
                </th>
              </tr>
            </thead>
            <tbody>
              {benefits.map(([challenge, help]) => (
                <tr
                  key={challenge}
                  className="border-t border-gray-200 bg-white even:bg-gray-50"
                >
                  <td className="px-5 py-4 font-semibold text-primary w-[42%]">
                    {challenge}
                  </td>
                  <td className="px-5 py-4 text-secondary">{help}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-gray-50 py-12 lg:py-16">
        <div className="container text-primary">
          <SectionHeading>FAQs</SectionHeading>
          <div className="max-w-4xl mx-auto space-y-3">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={faq.question}
                  className="rounded-xl border border-quaternary/40 overflow-hidden bg-white"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    className={`flex w-full items-center justify-between gap-3 px-5 py-4 text-left font-semibold transition-all duration-300 ${
                      isOpen
                        ? "bg-quaternary text-tertiary"
                        : "bg-white text-quaternary"
                    }`}
                  >
                    {faq.question}
                    {isOpen ? (
                      <FaMinus className="shrink-0" />
                    ) : (
                      <FaPlus className="shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <p className="px-5 py-4 text-secondary leading-7">
                      {faq.answer}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-[#0b2b4f] to-quaternary py-16 lg:py-20">
        <div className="container text-center">
          <h2 className="text-2xl lg:text-4xl font-bold text-tertiary pb-4">
            Ready to run exams you can trust?
          </h2>
          <p className="text-white/80 pb-8 lg:text-lg">
            Let IQSPL set up Exam Online for your institution or organization.
          </p>
          <ButtonSecondary onClick={openCalendly}>Book a demo</ButtonSecondary>
        </div>
      </section>
    </>
  );
};

export default Content;
